"use client";

import React, { useState, useEffect } from "react";
import Image from "next/image";
import { TextAnimate } from "./TextAnimation";
import SectionBadge from "./SectionBadge";
import { motion } from "motion/react";
import type { LunchSeatingSectionData } from "@/types/cms";
import { useSectionTracking } from "@/lib/analytics/use-section-tracking";

interface LunchSeatingSectionProps {
  data: LunchSeatingSectionData;
}

export default function LunchSeatingSection({
  data,
}: LunchSeatingSectionProps) {
  const sectionRef = useSectionTracking(
    "Lunch Seating Section",
    "lunch-seating",
  );
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkDevice = () => {
      const width = window.innerWidth;
      setIsMobile(width < 768);
    };

    checkDevice();

    let timeoutId: NodeJS.Timeout;
    const throttledResize = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(checkDevice, 150);
    };

    window.addEventListener("resize", throttledResize);
    return () => {
      window.removeEventListener("resize", throttledResize);
      clearTimeout(timeoutId);
    };
  }, []);

  const title = data.title;
  const description = data.description;

  return (
    <section
      ref={sectionRef as React.RefObject<HTMLElement>}
      id="lunch-seating"
      className="relative bg-linear-to-b from-black via-neutral-900 to-black py-24 px-4 lg:px-16 font-host-grotesk overflow-hidden"
    >
      <div className="absolute inset-0 opacity-20">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `linear-gradient(to right, #3a3a3a 1px, transparent 1px),
                           linear-gradient(to bottom, #3a3a3a 1px, transparent 1px)`,
            backgroundSize: "60px 60px",
          }}
        />
      </div>

      <div className="relative max-w-330 mx-auto">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="text-left"
          >
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-4xl md:text-4xl lg:text-5xl mb-6 text-white leading-tight"
            >
              {title}
            </motion.h2>

            {isMobile ? (
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.3 }}
                className="text-white/60 text-lg md:text-xl lg:text-2xl leading-relaxed font-normal"
              >
                {description}
              </motion.p>
            ) : (
              <TextAnimate
                animation="blurInUp"
                by="word"
                className="text-white/60 text-lg md:text-xl lg:text-2xl leading-relaxed font-normal"
                as="p"
                delay={0.3}
                duration={0.05}
                once={true}
              >
                {description}
              </TextAnimate>
            )}
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: "easeOut", delay: 0.2 }}
            className="w-full"
          >
            <Image
              src={data.image?.url || "/illustrations/table.jpg"}
              alt={data.imageAlt || "Seating Dashboard"}
              width={1200}
              height={900}
              className="w-full h-auto rounded-2xl"
            />
          </motion.div>
        </div>
      </div>
    </section>
  );
}
